import type { SetupConfirmations } from "@fomofi/shared";
import apiClient from "./client";

export type PayFrequency = "weekly" | "biweekly" | "semimonthly" | "monthly" | "irregular";
/** "code" (USD 1,234) was retired in BEN-141; every amount shows "$1,234". The
 *  field stays on the profile so the API contract is unchanged. */
export type CurrencyDisplay = "symbol";
export type DefaultView = "dashboard" | "transactions" | "budgets";
/** How many months the daily forecast projects (#420). Three options, matching
 *  the backend's `ForecastHorizon` choices — an arbitrary number would let a
 *  client ask for years of daily rows across three scenarios. */
export const FORECAST_HORIZONS = [3, 6, 12] as const;
export type ForecastHorizon = (typeof FORECAST_HORIZONS)[number];

export interface UserProfilePayload {
  /** The cadence the pay periods are built on: the detected paycheck's when
   *  there is one (`pay_frequency_source: "paycheck"`), else the saved value. */
  pay_frequency: PayFrequency;
  /** Read-only. Optional for a backend predating the field. */
  pay_frequency_source?: "paycheck" | "profile";
  needs_percentage: number;
  wants_percentage: number;
  savings_percentage: number;
  onboarding_complete: boolean;
  currency_display: CurrencyDisplay;
  compact_mode: boolean;
  default_view: DefaultView;
  /** Forecast Settings: the projection horizon in months (#420). */
  forecast_months: ForecastHorizon;
  /** AN-52: the one theme setting. Null until the account chooses (or a client writes up
   *  the device's choice). Optional for a backend predating the field. */
  theme?: "light" | "dark" | null;
  /** RD-35: when the one-time "This is your Home" banner was dismissed, on the account
   *  so it never returns on another device. Null until then. The banner fires only on
   *  "Use this plan", which arrives with Plan's spec, so nothing writes it yet.
   *  Optional for a backend predating the field. */
  home_intro_seen_at?: string | null;
  /** HP-21/H7: when Home was last opened, on the server. Written only by
   *  `recordHomeVisit`; read-only here. Optional for an older backend. */
  home_last_visit_at?: string | null;
  /** RD-32: when the first-week checklist was dismissed; null while it may show. */
  home_checklist_dismissed_at?: string | null;
  /** #733 (AN-34): what the user has confirmed in setup. Read-only here; written
   *  through `/accounts/setup-confirmations/`. Optional for an older backend. */
  setup_confirmations?: SetupConfirmations;
  /** Doc 3 G2: the walkthrough step the user closed at partway (1–10); null when never
   *  opened or step 10 was finished. On the account, so it resumes on any device. */
  walkthrough_last_step?: number | null;
  /** Doc 3 G5: when screen 10 was first reached. Analytics only. */
  walkthrough_completed_at?: string | null;
  /** Doc 3 G3: × on the Plan first-visit card, or the plan was confirmed. Never turns off. */
  plan_help_card_dismissed?: boolean;
  /** Doc 3 G4: the first payday check-in is finished. Never turns off. */
  first_payday_check_in_done?: boolean;
  /** #2165: the IANA zone the server's "today" follows (Plan's header, pay periods, "due
   *  today"). Null until a client sends one; written by `AccountTimezoneSync` each session.
   *  An unknown name is a 400. Optional for an older backend. */
  timezone?: string | null;
}

export interface UserMe {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  /** A shared demo persona. Its name is locked: a rename would reach every
   *  visitor, and the webhook ignores one anyway. Optional for a backend
   *  predating the field. */
  is_demo?: boolean;
  /** An allowlisted test account (fomotestN, acceptance1-5) that may reset itself to
   *  the client's workbook (Paul, 2026-10-09). Optional for an older backend. */
  can_reset_test_data?: boolean;
  /** Report 145: a bank of the user's needs re-authentication or has ended and no
   *  paycheck shows, so "no paycheck" surfaces say "Can't reach your bank." with
   *  Reconnect. The server's predicate. Optional for an older backend. */
  bank_unreachable?: boolean;
  /** Anthony's redesign (RD-08) and the Activity badge (RD-02). `redesign_nav` is the
   *  server switch (on for dev, or a named account); `activity_to_sort_count` is this
   *  pay period's purchases still needing a category, the one number the badge, Home's
   *  to-sort count and the first-week checklist all show. No UI reads it yet.
   *  Optional for an older backend. */
  features?: UserFeatures;
  profile: UserProfilePayload;
}

export interface UserFeatures {
  redesign_nav: boolean;
  activity_to_sort_count: number;
}

/** Put this test account back to the workbook, ending last month (POST, confirmed). */
export async function resetTestData(): Promise<{ detail: string }> {
  const { data } = await apiClient.post<{ detail: string }>("/accounts/test-data/reset/", {
    confirm: true,
  });
  return data;
}

export interface ProfileUpdatePayload {
  profile: Partial<UserProfilePayload>;
}

export async function getProfile(): Promise<UserMe> {
  const { data } = await apiClient.get<UserMe>("/profile/me/");
  return data;
}

export async function updateProfile(payload: ProfileUpdatePayload): Promise<UserMe> {
  const { data } = await apiClient.patch<UserMe>("/profile/me/", payload);
  return data;
}

/** HP-21: what is new since the previous visit to Home. `show` is false on a first
 *  visit, within 12 hours of the last one, or when nothing new arrived. */
export interface HomeVisit {
  previous_visit_at: string | null;
  show: boolean;
  purchases: number;
  spent: string;
  bills_paid: string[];
}

/** Record a visit to Home (H7: the last visit lives on the server). */
export async function recordHomeVisit(): Promise<HomeVisit> {
  const { data } = await apiClient.post<HomeVisit>("/accounts/home-visit/");
  return data;
}
